import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import KBContentDto from '@/models/KnowledgeBase/KBContentDto';
import CreateKBContentRequest from '@/models/KnowledgeBase/CreateKBContentRequest';
import UpdateKBContentDraftRequest from '@/models/KnowledgeBase/UpdateKBContentDraftRequest';
import PublishContentRequest from '@/models/KnowledgeBase/PublishContentRequest';
import RevertContentDraftRequest from '@/models/KnowledgeBase/RevertContentDraftRequest';

export interface ISysKBContentDetailsState {
  loading: boolean;
  content: KBContentDto | null;
  loadingDialog: boolean;
}

@Module({ dynamic: true, store, name: 'SysKBContentDetails', namespaced: true })
class SysKBContentDetails extends VuexModule implements ISysKBContentDetailsState {

  public loading = false;
  public content: KBContentDto | null = null;
  public loadingDialog = false;

  @Action
  async getContent(contentId: string) {
    this.setLoading(true);
    return ajax.get(`/api/v1/sys/kb/content/${contentId}`)
      .then(response => {
        this.context.commit('setContent', response.data);
        this.setLoading(false);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoading(false);
        throw error;
      });
  }

  @Action
  async createContent(request: CreateKBContentRequest) {
    this.setLoadingDialog(true);
    return ajax.post('/api/v1/sys/kb/content', request)
      .then(response => {
        this.setLoadingDialog(false);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingDialog(false);
        throw error;
      });
  }

  @Action
  async updateContentDraft(params: { contentId: string; request: UpdateKBContentDraftRequest }) {
    this.setLoadingDialog(true);
    return ajax.put(`/api/v1/sys/kb/content/${params.contentId}/draft`, params.request)
      .then(response => {
        this.setLoadingDialog(false);
        // Refresh current content
        this.getContent(params.contentId);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingDialog(false);
        throw error;
      });
  }

  @Action
  async publishContent(params: { contentId: string; request: PublishContentRequest }) {
    this.setLoadingDialog(true);
    return ajax.post(`/api/v1/sys/kb/content/${params.contentId}/publish`, params.request)
      .then(response => {
        this.setLoadingDialog(false);
        // Refresh current content
        this.getContent(params.contentId);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingDialog(false);
        throw error;
      });
  }

  @Action
  async deleteContent(params: { contentId: string; permanently?: boolean }) {
    this.setLoadingDialog(true);
    const url = `/api/v1/sys/kb/content/${params.contentId}` + (params.permanently ? '?permanently=true' : '');
    return ajax.delete(url)
      .then(response => {
        this.setLoadingDialog(false);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingDialog(false);
        throw error;
      });
  }

  @Action
  async revertContentDraft(params: { contentId: string; request: RevertContentDraftRequest }) {
    this.setLoadingDialog(true);
    return ajax.put(`/api/v1/sys/kb/content/${params.contentId}/revert`, params.request)
      .then(response => {
        this.setLoadingDialog(false);
        // Refresh current content to get updated draft
        this.getContent(params.contentId);
        return response.data;
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingDialog(false);
        throw error;
      });
  }

  @Action
  cleanState() {
    this.context.commit('resetState');
  }

  @Mutation
  resetState() {
    this.content = null;
    this.loading = false;
    this.loadingDialog = false;
  }

  @Mutation
  private setLoading(loading: boolean) {
    this.loading = loading;
  }

  @Mutation
  private setContent(content: KBContentDto) {
    this.content = content;
  }

  @Mutation
  private setLoadingDialog(loading: boolean) {
    this.loadingDialog = loading;
  }
}

export const SysKBContentDetailsModule = getModule(SysKBContentDetails);
